/**
 * Welle 124 — Nenngeldnachforderungen des TCH (Reiter „Nenngeld" unter Rechnungen).
 * 1. Turnier: nuLiga-CSV „Spieleranmeldungen - <Nr> - <Name>.csv" einlesen oder ein eingelesenes Turnier wählen;
 *    Name / Datum / Betrag änderbar (bleiben je Turnier gespeichert).
 * 2. Spieler anhaken (Filter) — oder Person aus dem Bestand suchen bzw. von Hand eintragen.
 * 3. Auswahl prüfen (Anrede, E-Mail) → Forderungen erstellen (Nummern ab 1160, Rechnungsausgangsbuch)
 *    → herunterladen (Batch Output + ZIP) oder per E-Mail senden (TCH-Ordner + Kopie DATEV).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "../../api/config";
import { json, euro, deDatum } from "./hilfen";
import { VersandProtokoll } from "./MailVersand";
import Ico from "./Ico";
import "../mietverwaltung/Tab5Bescheinigungen.css";
import "./Rechnungen.css";
import "./Nenngeld.css";

const NG = `${API_BASE}/mietverwaltung/api/nenngeld`;
const heute = () => new Date().toISOString().slice(0, 10);
const LEER_PERSON = { anrede: "", vorname: "", nachname: "", email: "", strasse: "", plz: "", ort: "" };

export default function NenngeldPanel({ onBuchGeaendert }) {
  const [turniere, setTurniere] = useState([]);
  const [turnier, setTurnier] = useState(null);           // {id, turnier_nr, name, datum, betrag}
  const [spieler, setSpieler] = useState([]);
  const [auswahl, setAuswahl] = useState([]);            // [{...spieler, anrede, email}]
  const [filter, setFilter] = useState({ q: "", konkurrenz: "", feld: "" });
  const [datum, setDatum] = useState(heute());
  const [ziel, setZiel] = useState(14);
  const [bestandQ, setBestandQ] = useState("");
  const [bestand, setBestand] = useState([]);
  const [hand, setHand] = useState(null);                // Formular „von Hand"
  const [neuT, setNeuT] = useState(null);                // Hotfix 6.20.3: Veranstaltung von Hand {name, datum, betrag, turnier_nr}
  const [ergebnis, setErgebnis] = useState([]);          // erstellte Forderungen
  const [mail, setMail] = useState({ verfuegbar: false });
  const [busy, setBusy] = useState(false);
  const [ziehen, setZiehen] = useState(false);
  // Rückfrage auf der Seite statt window.confirm (in der Desktop-App gibt es keine Browser-Dialoge)
  const [frage, setFrage] = useState(null);              // {wo: "erstellen"|"senden", text, zeilen[], ja}           // CSV wird über das Feld gezogen
  const [meldung, setMeldung] = useState(null);
  const [protokoll, setProtokoll] = useState([]);        // Welle 126: was beim Senden passiert ist
  const zeige = (ok, text, unten = false) => setMeldung({ ok, text, unten });   // unten = am Erstellen-/Senden-Knopf

  const turniereLaden = useCallback(() => {
    fetch(`${NG}/turniere`).then(json).then((d) => setTurniere(d.turniere || [])).catch(() => {});
  }, []);
  useEffect(() => {
    turniereLaden();
    fetch(`${NG}/mail-status`).then(json).then(setMail).catch(() => {});
  }, [turniereLaden]);

  const turnierWaehlen = async (t) => {
    // von Hand/aus dem Bestand Hinzugefügte (ohne Turnier) bleiben beim Turnierwechsel ausgewählt
    setTurnier(t); setAuswahl((p) => p.filter((a) => !a.turnier_id)); setErgebnis([]); setFilter({ q: "", konkurrenz: "", feld: "" });
    if (!t) { setSpieler([]); return; }
    try { setSpieler((await json(await fetch(`${NG}/spieler?turnier=${t.id}`))).spieler || []); }
    catch (e) { zeige(false, String(e.message || e)); }
  };

  const csvHochladen = async (dateien) => {
    const datei = dateien[0];
    if (!datei) return;
    if (!/\.csv$/i.test(datei.name)) { zeige(false, `„${datei.name}“ ist keine CSV-Datei.`); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", datei);
      const d = await json(await fetch(`${NG}/csv`, { method: "POST", body: fd }));
      setTurnier(d.turnier); setSpieler(d.spieler || []); setAuswahl([]); setErgebnis([]);
      turniereLaden();
      zeige(true, `${d.eingelesen} Anmeldungen aus „${datei.name}“ eingelesen — Turnier ${d.turnier.turnier_nr}.`
        + (d.turnier.datum ? "" : " Bitte Turnierdatum und Betrag prüfen."));
    } catch (e) { zeige(false, String(e.message || e)); }
    setBusy(false);
  };

  const turnierSpeichern = async (felder) => {
    const neu = { ...turnier, ...felder };
    setTurnier(neu);
    try {
      const d = await json(await fetch(`${NG}/turniere/${turnier.id}`, { method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: neu.name, datum: neu.datum || null, betrag: neu.betrag }) }));
      setTurnier(d); turniereLaden();
    } catch (e) { zeige(false, String(e.message || e)); }
  };

  // Auswahl
  const istGewaehlt = (s) => auswahl.some((a) => a.id === s.id);
  const umschalten = (s) => setAuswahl((p) => (p.some((a) => a.id === s.id) ? p.filter((a) => a.id !== s.id)
    : [...p, { ...s }]));
  const aendern = (id, felder) => setAuswahl((p) => p.map((a) => (a.id === id ? { ...a, ...felder } : a)));

  const konkurrenzen = useMemo(() => [...new Set(spieler.map((s) => s.konkurrenz).filter(Boolean))].sort(), [spieler]);
  const felder = useMemo(() => [...new Set(spieler.map((s) => s.feld).filter(Boolean))].sort(), [spieler]);
  const sichtbar = spieler.filter((s) => {
    const q = filter.q.trim().toLowerCase();
    if (filter.konkurrenz && s.konkurrenz !== filter.konkurrenz) return false;
    if (filter.feld && s.feld !== filter.feld) return false;
    return !q || q.split(/\s+/).every((w) => `${s.name} ${s.verein || ""} ${s.email || ""} ${s.dtb_id || ""}`.toLowerCase().includes(w));
  });

  // Bestand / von Hand
  useEffect(() => {
    const q = bestandQ.trim();
    if (q.length < 2) { setBestand([]); return undefined; }
    const t = setTimeout(() => {
      fetch(`${NG}/bestand?q=${encodeURIComponent(q)}`).then(json).then((d) => setBestand(d.treffer || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [bestandQ]);
  const ausBestand = async (b) => {
    try {
      let s = b;
      if (!b.id) {                                         // Treffer aus „Adressen" → als Spieler anlegen
        s = await json(await fetch(`${NG}/spieler`, { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...b, turnier_id: turnier?.id || null }) }));
      }
      if (!istGewaehlt(s)) setAuswahl((p) => [...p, { ...s }]);
      setBestandQ(""); setBestand([]);
    } catch (e) { zeige(false, String(e.message || e)); }
  };
  const turnierAnlegen = async () => {
    try {
      const d = await json(await fetch(`${NG}/turniere`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...neuT, betrag: String(neuT.betrag || "").replace(",", ".") }) }));
      setNeuT(null); turniereLaden();
      await turnierWaehlen({ anzahl: 0, ...d });
      zeige(true, `Veranstaltung „${d.name}“ (Nr. ${d.turnier_nr}) angelegt — jetzt Personen hinzufügen.`);
    } catch (e) { zeige(false, String(e.message || e)); }
  };
  const handSpeichern = async () => {
    try {
      const s = await json(await fetch(`${NG}/spieler`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...hand, turnier_id: turnier?.id || null }) }));
      setAuswahl((p) => [...p, { ...s }]); setHand(null);
      zeige(true, `${s.name} hinzugefügt.`);
    } catch (e) { zeige(false, String(e.message || e)); }
  };

  const erstellenFragen = () => {
    if (!turnier || !auswahl.length) return;
    setFrage({ wo: "erstellen", text: `${auswahl.length} Nenngeldnachforderung(en) über je ${euro(turnier.betrag)} erstellen?`,
      zeilen: ["Sie bekommen fortlaufende Nummern und stehen danach im Rechnungsausgangsbuch.",
        ...(turnier.datum ? [] : ["Achtung: Turnierdatum fehlt — es steht dann nicht im Titel."])],
      ja: erstellen });
  };
  const erstellen = async () => {
    setFrage(null);
    if (!turnier || !auswahl.length) return;
    setBusy(true);
    try {
      const d = await json(await fetch(`${NG}/erstellen`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ turnier_id: turnier.id, datum, zahlungsziel: Number(ziel) || 14,
          personen: auswahl.map((a) => ({ spieler_id: a.id, anrede: a.anrede || "", email: a.email || "" })) }) }));
      setErgebnis(d.forderungen || []); setAuswahl([]);
      onBuchGeaendert && onBuchGeaendert();
      zeige(true, `${(d.forderungen || []).length} Forderung(en) erstellt: Nr. ${(d.forderungen || []).map((f) => f.rg_nr).join(", ")}.`, true);
    } catch (e) { zeige(false, String(e.message || e), true); }
    setBusy(false);
  };

  const ansehen = async (f) => {
    try {
      const r = await fetch(`${NG}/${f.id}/pdf`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { zeige(false, String(e.message || e), true); }
  };
  const herunterladen = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${NG}/herunterladen`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: ergebnis.map((f) => f.id) }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Download fehlgeschlagen");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(await r.blob());
      a.download = `Nenngeldnachforderungen ${heute()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      const h = decodeURIComponent(r.headers.get("X-Hinweise") || "");
      zeige(!h, `${ergebnis.length} PDF(s) heruntergeladen und im Batch Output abgelegt.${h ? ` Hinweis: ${h}` : ""}`, true);
    } catch (e) { zeige(false, String(e.message || e), true); }
    setBusy(false);
  };
  const sendenFragen = () => {
    const ohne = ergebnis.filter((f) => !f.email);
    setFrage({ wo: "senden", text: `${ergebnis.length - ohne.length} Nenngeldnachforderung(en) per E-Mail senden?`,
      zeilen: [...(ohne.length ? [`Ohne E-Mail-Adresse (werden NICHT gesendet): ${ohne.map((f) => f.name).join(", ")}`] : []),
        "Danach liegen die PDFs im TCH-Ordner „Nenngeldnachforderungen“, eine Kopie für DATEV."],
      ja: senden });
  };
  const senden = async () => {
    setFrage(null);
    setBusy(true);
    try {
      const d = await json(await fetch(`${NG}/senden`, { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: ergebnis.filter((f) => f.email).map((f) => f.id) }) }));
      const an = new Set((d.gesendet || []).map((g) => g.id));
      setErgebnis((p) => p.map((f) => (an.has(f.id) ? { ...f, gesendet: true } : f)));
      setProtokoll(d.protokoll || []);
      onBuchGeaendert && onBuchGeaendert();
      zeige(!(d.fehler || []).length, `${(d.gesendet || []).length} gesendet.`
        + ((d.fehler || []).length ? ` Nicht gesendet: ${d.fehler.join(" · ")}` : ""), true);
    } catch (e) { zeige(false, String(e.message || e), true); }
    setBusy(false);
  };

  const summe = auswahl.length * Number(turnier?.betrag || 0);
  return (
    <div className="rg-wrap ng">
      <div className="mb-sektion">
        <div className="mb-sektion-titel">Nenngeldnachforderungen · TCH Buchholz e.V.</div>
        <div className="ng-turnier-wahl">
          {/* CSV per Klick oder Drag & Drop (gleiche Größe wie ein Knopf) */}
          <label className={`mb-btn ng-drop${ziehen ? " aktiv" : ""}`} title="CSV hier ablegen oder klicken"
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; setZiehen(true); }}
            onDragEnter={(e) => { e.preventDefault(); setZiehen(true); }}
            onDragLeave={() => setZiehen(false)}
            onDrop={(e) => { e.preventDefault(); setZiehen(false); csvHochladen(Array.from(e.dataTransfer.files || [])); }}>
            <Ico n="hochladen" /> {busy ? "liest …" : "CSV hierher ziehen oder klicken (nuLiga „Spieleranmeldungen“)"}
            <input type="file" accept=".csv,text/csv" hidden onChange={(e) => { const d = Array.from(e.target.files || []); e.target.value = ""; csvHochladen(d); }} />
          </label>
          <span className="rg-klein" style={{ margin: 0 }}>oder</span>
          <select className="mb-input" value={neuT ? "neu" : (turnier?.id || "")}
            onChange={(e) => { if (e.target.value === "neu") { setNeuT({ name: "", datum: heute(), betrag: "", turnier_nr: "" }); return; }
              setNeuT(null); turnierWaehlen(turniere.find((t) => String(t.id) === e.target.value) || null); }}>
            <option value="">— eingelesenes Turnier wählen —</option>
            <option value="neu">＋ Veranstaltung von Hand anlegen …</option>
            {turniere.map((t) => <option key={t.id} value={t.id}>{t.turnier_nr} · {t.name}{t.datum ? ` · ${deDatum(t.datum)}` : ""} ({t.anzahl})</option>)}
          </select>
        </div>
        {neuT && (
          <div className="mb-felder ng-turnier">
            <label className="mb-feld ng-breit"><span className="mb-label">Name der Veranstaltung (im Titel)</span>
              <input className="mb-input" value={neuT.name} placeholder="z. B. 26. Orange & Green Cup" autoFocus
                onChange={(e) => setNeuT({ ...neuT, name: e.target.value })} /></label>
            <label className="mb-feld"><span className="mb-label">Datum</span>
              <input className="mb-input" type="date" value={neuT.datum} onChange={(e) => setNeuT({ ...neuT, datum: e.target.value })} /></label>
            <label className="mb-feld"><span className="mb-label">Nenngeld (€)</span>
              <input className="mb-input" inputMode="decimal" value={neuT.betrag} placeholder="z. B. 30"
                onChange={(e) => setNeuT({ ...neuT, betrag: e.target.value })} /></label>
            <label className="mb-feld"><span className="mb-label">Turnier-Nr. (nuLiga, optional)</span>
              <input className="mb-input" inputMode="numeric" value={neuT.turnier_nr} placeholder="leer = eigene Nummer"
                onChange={(e) => setNeuT({ ...neuT, turnier_nr: e.target.value.replace(/\D/g, "") })} /></label>
            <div className="rg-knoepfe" style={{ gridColumn: "1 / -1" }}>
              <button type="button" className="mb-btn primary" disabled={!neuT.name.trim() || !String(neuT.betrag).trim()} onClick={turnierAnlegen}>Veranstaltung anlegen</button>
              <button type="button" className="mb-link" onClick={() => setNeuT(null)}>abbrechen</button>
            </div>
          </div>
        )}
        {turnier && !neuT && (
          <div className="mb-felder ng-turnier">
            <label className="mb-feld"><span className="mb-label">Turnier-Nr.</span><input className="mb-input" value={turnier.turnier_nr} disabled /></label>
            <label className="mb-feld ng-breit"><span className="mb-label">Turniername (im Titel)</span>
              <input className="mb-input" value={turnier.name || ""} onChange={(e) => setTurnier({ ...turnier, name: e.target.value })}
                onBlur={(e) => turnierSpeichern({ name: e.target.value })} /></label>
            <label className="mb-feld"><span className="mb-label">Turnierdatum</span>
              <input className="mb-input" type="date" value={turnier.datum || ""} onChange={(e) => turnierSpeichern({ datum: e.target.value })} /></label>
            <label className="mb-feld"><span className="mb-label">Nenngeld (€)</span>
              <input className="mb-input" value={turnier.betrag ?? ""} onChange={(e) => setTurnier({ ...turnier, betrag: e.target.value })}
                onBlur={(e) => turnierSpeichern({ betrag: String(e.target.value).replace(",", ".") })} /></label>
            <label className="mb-feld"><span className="mb-label">Rechnungsdatum</span>
              <input className="mb-input" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} /></label>
            <label className="mb-feld"><span className="mb-label">Zahlungsziel (Tage)</span>
              <input className="mb-input" inputMode="numeric" value={ziel} onChange={(e) => setZiel(e.target.value.replace(/\D/g, ""))} /></label>
          </div>
        )}
      </div>

      {meldung && !meldung.unten && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}

      {turnier && (
        <div className="mb-sektion">
          <div className="mb-sektion-titel">Spieler ({sichtbar.length} von {spieler.length})</div>
          <div className="ng-filter">
            <input className="mb-input" placeholder="Name, Verein, E-Mail, ID …" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} />
            <select className="mb-input" value={filter.konkurrenz} onChange={(e) => setFilter({ ...filter, konkurrenz: e.target.value })}>
              <option value="">alle Konkurrenzen</option>{konkurrenzen.map((k) => <option key={k}>{k}</option>)}
            </select>
            <select className="mb-input" value={filter.feld} onChange={(e) => setFilter({ ...filter, feld: e.target.value })}>
              <option value="">Hauptfeld + Nachrücker</option>{felder.map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div className="rg-tabelle-rahmen ng-liste">
            <table className="rg-tabelle">
              <thead><tr><th /><th>Name</th><th>Konkurrenz</th><th>Verein</th><th>E-Mail</th><th>Feld</th><th>Bestand</th></tr></thead>
              <tbody>
                {sichtbar.map((s) => (
                  <tr key={s.id} className={istGewaehlt(s) ? "ng-gewaehlt" : ""} onClick={() => umschalten(s)}>
                    <td><input type="checkbox" readOnly checked={istGewaehlt(s)} /></td>
                    <td><b>{s.name}</b>{s.geburtsdatum && <span className="rg-klein" style={{ margin: 0 }}> · {deDatum(s.geburtsdatum)}</span>}</td>
                    <td>{s.konkurrenz}</td><td>{s.verein}</td><td>{s.email || <i className="rg-klein">—</i>}</td><td>{s.feld}</td>
                    <td>{(s.forderungen || []).length > 0 && <span className="ng-badge" title={`Frühere Forderungen: ${s.forderungen.join(", ")}`}>Forderung {s.forderungen.join(", ")}</span>}
                      {(s.andere_turniere || []).length > 0 && <span className="ng-badge grau" title={s.andere_turniere.join(", ")}>{s.andere_turniere.length} weitere Turniere</span>}</td>
                  </tr>
                ))}
                {!sichtbar.length && <tr><td colSpan={7} className="rg-klein">Keine Spieler.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mb-sektion">
        <div className="mb-sektion-titel">Person hinzufügen</div>
        <div className="ng-filter">
          <div className="ng-bestand">
            <input className="mb-input" placeholder="Im Bestand suchen (alle eingelesenen Turniere + Adressen) …" value={bestandQ}
              onChange={(e) => setBestandQ(e.target.value)} />
            {bestand.length > 0 && (
              <div className="rg-baustein-liste">
                {bestand.map((b, i) => (
                  <button type="button" key={`${b.id || "a"}-${b.adresse_id || i}`} className="rg-such-treffer" onMouseDown={(e) => { e.preventDefault(); ausBestand(b); }}>
                    <b>{b.name}</b> <span className="rg-baustein-vorschau">{[b.turnier, b.verein, b.email, (b.forderungen || []).length ? `Forderung ${b.forderungen.join(", ")}` : ""].filter(Boolean).join(" · ")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {!hand && <button type="button" className="mb-btn" onClick={() => setHand({ ...LEER_PERSON })}><Ico n="plus" /> von Hand eintragen</button>}
        </div>
        {hand && (
          <div className="mb-felder" style={{ marginTop: 8 }}>
            <label className="mb-feld"><span className="mb-label">Anrede</span>
              <select className="mb-input" value={hand.anrede} onChange={(e) => setHand({ ...hand, anrede: e.target.value })}>
                <option value="">(raten)</option><option>Herr</option><option>Frau</option></select></label>
            {[["vorname", "Vorname"], ["nachname", "Nachname"], ["email", "E-Mail"], ["strasse", "Straße (optional)"], ["plz", "PLZ"], ["ort", "Ort"]].map(([k, l]) => (
              <label key={k} className="mb-feld"><span className="mb-label">{l}</span>
                <input className="mb-input" value={hand[k]} onChange={(e) => setHand({ ...hand, [k]: e.target.value })} /></label>
            ))}
            <div className="rg-knoepfe" style={{ gridColumn: "1 / -1" }}>
              <button type="button" className="mb-btn primary" disabled={!hand.nachname.trim()} onClick={handSpeichern}>Hinzufügen</button>
              <button type="button" className="mb-link" onClick={() => setHand(null)}>abbrechen</button>
            </div>
          </div>
        )}
      </div>

      <div className="mb-sektion">
        <div className="mb-sektion-titel">Ausgewählt ({auswahl.length}){auswahl.length > 0 && turnier ? ` · ${euro(summe)}` : ""}</div>
        {!auswahl.length ? <p className="rg-klein">Noch niemand ausgewählt — oben in der Liste anhaken oder eine Person hinzufügen.</p> : (
          <div className="rg-tabelle-rahmen">
            <table className="rg-tabelle">
              <thead><tr><th>Anrede</th><th>Name</th><th>E-Mail</th><th>Konkurrenz</th><th /></tr></thead>
              <tbody>
                {auswahl.map((a) => (
                  <tr key={a.id}>
                    <td><select className="mb-input" value={a.anrede || ""} onChange={(e) => aendern(a.id, { anrede: e.target.value })}>
                      <option value="">—</option><option>Herr</option><option>Frau</option></select></td>
                    <td><b>{a.name}</b></td>
                    <td><input className="mb-input" value={a.email || ""} placeholder="keine — nur Download" onChange={(e) => aendern(a.id, { email: e.target.value })} /></td>
                    <td>{a.konkurrenz}</td>
                    <td><button type="button" className="mb-link" title="entfernen" onClick={() => umschalten(a)}><Ico n="schliessen" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mb-buttons">
          <button className="mb-btn primary" disabled={busy || !turnier || !auswahl.length} onClick={erstellenFragen}
            title={!turnier ? "Erst ein Turnier einlesen oder wählen" : ""}>
            {busy ? "arbeitet …" : `Nenngeldnachforderungen erstellen (${auswahl.length})`}</button>
        </div>
        {frage?.wo === "erstellen" && <Rueckfrage frage={frage} onNein={() => setFrage(null)} />}
        {meldung?.unten && !ergebnis.length && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}
      </div>

      {ergebnis.length > 0 && (
        <div className="mb-sektion">
          <div className="mb-sektion-titel">Erstellt ({ergebnis.length})</div>
          <ul className="ng-ergebnis">
            {ergebnis.map((f) => (
              <li key={f.id}><b>{f.rg_nr}</b> · {f.name} · {f.email || <i className="rg-klein">ohne E-Mail</i>}
                {" "}<button type="button" className="mb-link" onClick={() => ansehen(f)}>ansehen</button>
                {f.gesendet && <span className="ng-badge">gesendet</span>}
                <div className="rg-klein" style={{ margin: 0 }}>{f.dateiname}</div></li>
            ))}
          </ul>
          <div className="mb-buttons">
            <button className="mb-btn" disabled={busy} onClick={herunterladen}><Ico n="runter" /> Herunterladen (ZIP)</button>
            <button className="mb-btn primary" disabled={busy || !mail.verfuegbar} onClick={sendenFragen}
              title={mail.verfuegbar ? `Absender ${mail.absender}` : mail.hinweis}><Ico n="verbinden" /> Per E-Mail senden</button>
          </div>
          {frage?.wo === "senden" && <Rueckfrage frage={frage} onNein={() => setFrage(null)} />}
          {meldung?.unten && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}
          {protokoll.length > 0 && <><div className="rg-klein" style={{ marginBottom: 0 }}>Das wurde gemacht:</div><VersandProtokoll protokoll={protokoll} /></>}
          {!mail.verfuegbar && <p className="rg-klein">{mail.hinweis}</p>}
        </div>
      )}
    </div>
  );
}


function Rueckfrage({ frage, onNein }) {
  return (
    <div className="ng-frage">
      <b>{frage.text}</b>
      {(frage.zeilen || []).map((z) => <div key={z} className="rg-klein" style={{ margin: 0 }}>{z}</div>)}
      <div className="rg-knoepfe">
        <button type="button" className="mb-btn primary" onClick={frage.ja}>Ja</button>
        <button type="button" className="mb-btn" onClick={onNein}>Abbrechen</button>
      </div>
    </div>
  );
}
